import type { TFunction } from 'i18next';

import { formatDateTime } from '~/utils/date';

import { humanizeCode, toWireConnector } from './catalog-labels';
import { parseSourceProductId } from './source-product-id';

/** Placeholder rendered for an absent value in tables and attribute lists. */
export const EMPTY_VALUE = '—';

/** Vocabularies owned by this feature with a label map under `labels.<kind>` in catalog-enrichment-curation. */
export type CurationLabelKind =
  | 'feedState'
  | 'availability'
  | 'condition'
  | 'imageRole'
  | 'imageStatus'
  | 'attention';

/** The backend's free-text "Other" attention reason (§145) — the one label whose display always
 *  tries to compose in the flagger's note, never a bare, unexplained "Other". */
export const OTHER_ATTENTION_LABEL_CODE = 'OTHER';

/**
 * Q8 — this feature's own enum codes (feed state, availability, condition, image
 * role/status) never render raw: looks up `labels.<kind>.<CODE>` in the
 * catalog-enrichment-curation namespace and falls back to a humanised code so a value the
 * feed/connector adds later still reads sensibly. Pass the `t` from
 * `useTranslation('catalog-enrichment-curation')`.
 *
 * §145 — the OTHER attention reason additionally takes the flagger's own note: wherever one is on
 * hand (a single flagged row, not an aggregate count across many), it renders "Other — <note>"
 * instead of a bare "Other" that would hide what was actually typed.
 */
export function curationLabel(
  t: TFunction<'catalog-enrichment-curation'>,
  kind: CurationLabelKind,
  code: string | null | undefined,
  note?: string | null,
): string {
  if (!code) return EMPTY_VALUE;
  if (kind === 'attention' && code === OTHER_ATTENTION_LABEL_CODE && note) {
    return t('labels.attentionOtherWithNote', { note });
  }
  return t(`labels.${kind}.${code}`, { defaultValue: humanizeCode(code) });
}

/** Price as the feed sent it: the decimal string and currency side by side, no float math. */
export function formatPrice(amount: string | null, currency: string | null): string {
  if (!amount) return EMPTY_VALUE;
  return currency ? `${amount} ${currency}` : amount;
}

/**
 * "24.64 GBP · In stock" for a feed row's price/availability, or null when both are
 * absent — feeds `groupedFieldSummary` so a grouped product's markets only print
 * when their price/availability actually differ (Q20).
 */
export function priceAvailabilityText(
  t: TFunction<'catalog-enrichment-curation'>,
  amount: string | null,
  currency: string | null,
  availability: string | null,
): string | null {
  const price = amount ? formatPrice(amount, currency) : null;
  const avail = availability ? curationLabel(t, 'availability', availability) : null;
  if (!price && !avail) return null;
  return [price, avail].filter((part): part is string => !!part).join(' · ');
}

/**
 * "24.64 GBP · Active" for a row that has a price and a feed state but no availability
 * (the skipped / already-curated lists) — null when both are absent.
 */
export function priceStateText(
  t: TFunction<'catalog-enrichment-curation'>,
  row: { priceAmount: string | null; priceCurrency: string | null; state: string | null },
): string | null {
  const price = row.priceAmount ? formatPrice(row.priceAmount, row.priceCurrency) : null;
  const state = row.state ? curationLabel(t, 'feedState', row.state) : null;
  if (!price && !state) return null;
  return [price, state].filter((part): part is string => !!part).join(' · ');
}

/** Date-time for an optional ISO timestamp, or the empty placeholder. */
export function formatOptionalDateTime(iso: string | null | undefined): string {
  return iso ? formatDateTime(iso) : EMPTY_VALUE;
}

/** First 8 characters of a uuid — enough to tell runs apart at a glance. */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : EMPTY_VALUE;
}

/** Bytes as KB / MB for image sizes. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return EMPTY_VALUE;
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Thousands-separated integer count. */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? EMPTY_VALUE : value.toLocaleString();
}

/**
 * R4 — "never raw snake_case keys as labels": turns a wire key like
 * `item_group_id` into "Item group id" for the version snapshots, whose keys
 * are open-ended and can't all be mapped through i18n ahead of time.
 */
export function humanizeKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A feed category string ("Apparel > Shoes > Sneakers") split for a breadcrumb. */
export function splitCategoryPath(category: string | null): string[] {
  if (!category) return [];
  return category
    .split('>')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** The bare host of a URL for a compact "domain ↗" link label; falls back to the raw link. */
export function linkDomain(link: string): string {
  try {
    return new URL(link).hostname.replace(/^www\./, '');
  } catch {
    return link;
  }
}

/** One catalog product's feed rows, grouped for display. */
export interface ProductRowGroup<T> {
  /** The matched product id, `fp:<feedProductId>` when unmatched, or `row:<index>` as a last resort. */
  key: string;
  /** The first row seen for this product — the source for title/image/shared fields. */
  primary: T;
  rows: T[];
}

/**
 * Q20 — "one row per product": several feed rows that are the same catalog product
 * (e.g. a US and a UK listing) collapse into one visual row/card. Preserves first-seen
 * order; a row with no matched product id falls back to its feed-product id, and to its
 * position when even that is blank (reconciliation rows with no feed-product match at
 * all) — so rows never merge just because both ids happen to be empty.
 */
export function groupRowsByProduct<T extends { productId: string | null; feedProductId: string }>(
  rows: T[],
): ProductRowGroup<T>[] {
  const groups: ProductRowGroup<T>[] = [];
  const byKey = new Map<string, ProductRowGroup<T>>();
  rows.forEach((row, index) => {
    const key =
      row.productId ?? (row.feedProductId ? `fp:${row.feedProductId}` : `row:${String(index)}`);
    const existing = byKey.get(key);
    if (existing) {
      existing.rows.push(row);
    } else {
      const group: ProductRowGroup<T> = { key, primary: row, rows: [row] };
      byKey.set(key, group);
      groups.push(group);
    }
  });
  return groups;
}

/** What a row's market label is read from: feed label + language when sent, else its connector id. */
export interface MarketLabelRow {
  sourceProductId?: string | null | undefined;
  feedLabel?: string | null | undefined;
  language?: string | null | undefined;
}

/**
 * Each row's market label within its group: `feed_label` when the row carries it — with the
 * language added when the same label appears in the group in more than one language ("CH · de",
 * "CH · fr") — else the country parsed from the connector id (older payloads have no feed label).
 */
export function groupMarketLabels(rows: readonly MarketLabelRow[]): (string | null)[] {
  const languagesByLabel = new Map<string, Set<string>>();
  for (const row of rows) {
    if (!row.feedLabel) continue;
    const languages = languagesByLabel.get(row.feedLabel) ?? new Set<string>();
    languages.add(row.language ?? '');
    languagesByLabel.set(row.feedLabel, languages);
  }
  return rows.map((row) => {
    if (row.feedLabel) {
      const several = (languagesByLabel.get(row.feedLabel)?.size ?? 0) > 1;
      return several && row.language ? `${row.feedLabel} · ${row.language}` : row.feedLabel;
    }
    return parseSourceProductId(row.sourceProductId)?.country ?? null;
  });
}

/** The distinct market labels a grouped product is listed in, in first-seen order. */
export function groupMarkets(rows: readonly MarketLabelRow[]): string[] {
  return [...new Set(groupMarketLabels(rows).filter((m): m is string => !!m))];
}

/**
 * Q20 — one field across a grouped product's rows: a single value when every row
 * agrees, else each row's value suffixed with its market label (`groupMarketLabels`) and
 * joined ("96.3 US · 94.1 UK"), so a shared value never repeats needlessly.
 */
export function groupedFieldSummary<T extends MarketLabelRow>(
  rows: T[],
  pick: (row: T) => string | null,
): string | null {
  const values = rows.map(pick);
  const unique = [...new Set(values.filter((v): v is string => v !== null))];
  if (unique.length <= 1) return unique[0] ?? null;
  const markets = groupMarketLabels(rows);
  return rows
    .map((row, i) => {
      const value = pick(row);
      if (value === null) return null;
      const market = markets[i];
      return market ? `${value} ${market}` : value;
    })
    .filter((s): s is string => s !== null)
    .join(' · ');
}

/** Minimal shape the grouped-row attention helpers below need from one feed row. */
interface AttentionGroupRow extends MarketLabelRow {
  feedProductId: string;
  sourceProductId: string | null;
  needsAttention: boolean;
}

/**
 * Bug fix — a grouped product's most actionable row: the first flagged member (e.g. the UK
 * listing carrying MISSING_REQUIRED_FIELDS) or, when nothing is flagged, the group's primary
 * row. The grouped row's title link and its flag chip must both open THIS row, never just the
 * first-seen one — the reported bug was a US+UK group whose chip named the UK flag but whose
 * title opened the clean US listing.
 */
export function groupActionableRow<T extends AttentionGroupRow>(primary: T, rows: T[]): T {
  return rows.find((r) => r.needsAttention) ?? primary;
}

/**
 * Every market a grouped product's rows are flagged in (e.g. `["UK"]`, by `groupMarketLabels`)
 * — feeds the flag chip's "<label> · UK" copy so it names WHERE the issue is, not just THAT one
 * exists.
 */
export function groupFlaggedMarkets(rows: AttentionGroupRow[]): string[] {
  const markets = groupMarketLabels(rows);
  return [
    ...new Set(
      rows.map((r, i) => (r.needsAttention ? markets[i] : null)).filter((c): c is string => !!c),
    ),
  ];
}

/**
 * `ProductCell`'s `marketHrefs` map for a grouped product: market label → that market's own
 * feed row, flagged when THAT specific row (not just some row in the group) carries the
 * attention flag — so a market chip only warns about the market it actually links to.
 */
export function groupMarketHrefs(
  rows: AttentionGroupRow[],
): Record<string, { feedProductId: string; flagged?: boolean }> {
  const hrefs: Record<string, { feedProductId: string; flagged?: boolean }> = {};
  const markets = groupMarketLabels(rows);
  rows.forEach((row, i) => {
    const market = markets[i];
    if (!market) return;
    hrefs[market] = { feedProductId: row.feedProductId, flagged: row.needsAttention };
  });
  return hrefs;
}

/** The eight Merchant Center fields a listing must carry; GTIN and MPN each satisfy the pair. */
export type GmcRequiredFieldKey =
  | 'title'
  | 'description'
  | 'link'
  | 'imageUrl'
  | 'price'
  | 'availability'
  | 'brand'
  | 'gtinOrMpn';

/** One required field's presence on the current listing. */
export interface GmcRequiredFieldCheck {
  key: GmcRequiredFieldKey;
  present: boolean;
}

/** Whether a feed source is a Merchant Center (GMC) connector — the required-fields check only
 *  makes sense there; other connectors (Meta, Shopify) don't share GMC's field requirements. */
export function isGmcConnector(feedSource: string | null | undefined): boolean {
  return toWireConnector(feedSource) === 'GMC';
}

/**
 * Fix B.2 — the Merchant Center required-fields check, computed from attributes already on the
 * page (no extra fetch): title, description, link, image link, price, availability, brand, and
 * GTIN-or-MPN (either one satisfies the pair). Lets the needs-attention card and the attribute
 * grid show the SAME verdict instead of re-deriving it twice.
 */
export function gmcRequiredFieldChecks(a: {
  title: string | null;
  description: string | null;
  link: string | null;
  imageUrl: string | null;
  priceAmount: string | null;
  availability: string | null;
  brand: string | null;
  gtin: string | null;
  mpn: string | null;
}): GmcRequiredFieldCheck[] {
  return [
    { key: 'title', present: !!a.title },
    { key: 'description', present: !!a.description },
    { key: 'link', present: !!a.link },
    { key: 'imageUrl', present: !!a.imageUrl },
    { key: 'price', present: !!a.priceAmount },
    { key: 'availability', present: !!a.availability },
    { key: 'brand', present: !!a.brand },
    { key: 'gtinOrMpn', present: !!a.gtin || !!a.mpn },
  ];
}

/**
 * The note attached to a feed row's CURRENT flag. Notes are recorded per curation-run-history
 * entry, not on the feed row itself, so this matches the newest run entry whose label code and
 * flagger agree with the row's current flag and returns its note — null when none match or the
 * matching entry left no note.
 */
export function findFlagNote(
  runs: {
    needsAttentionLabelCode?: string | null;
    needsAttentionUpdatedBy?: string | null;
    notes?: string | null;
  }[],
  flag: { labelCode: string | null; updatedBy: string | null },
): string | null {
  if (!flag.labelCode) return null;
  const match = runs.find(
    (run) =>
      run.needsAttentionLabelCode === flag.labelCode &&
      run.needsAttentionUpdatedBy === flag.updatedBy &&
      !!run.notes,
  );
  return match?.notes ?? null;
}

/** `feedLabel|language`: the market grain a product's items split on. */
function marketKey(row: { feedLabel?: string | null; language?: string | null }): string {
  return `${row.feedLabel ?? ''}|${row.language ?? ''}`;
}

/**
 * Whether a product group at a page edge may continue on the neighbouring page. False when its
 * rows already cover every market of the run, or when the neighbouring page (already cached)
 * shows a different product at that edge; true only when neither rules it out.
 */
export function mayContinueAcrossPage(
  groupRows: readonly {
    productId: string | null;
    feedLabel?: string | null;
    language?: string | null;
  }[],
  runMarkets: readonly { feedLabel?: string | null; language?: string | null }[],
  neighbourEdgeProductId: string | null | undefined,
): boolean {
  const productId = groupRows[0]?.productId ?? null;
  // An ungrouped item stands alone: there is nothing to continue.
  if (!productId) return false;
  if (runMarkets.length > 0) {
    const have = new Set(groupRows.map(marketKey));
    if (runMarkets.every((m) => have.has(marketKey(m)))) return false;
  }
  if (neighbourEdgeProductId !== undefined) return neighbourEdgeProductId === productId;
  return true;
}
